import { V1_BASE_URL } from '@app/data/constants';
import {
  getActivitiesByLeadUuid,
  type GetActivitiesByLeadUuidResponse,
} from '@app/queries/activities/getActivitiesByLeadUuid';
import { type TimeFilterOptionsType } from '@app/store/leadDetail/activitiesTab';
import { transformGraphqlResponse } from '@repo/utils';
import baseService, { TAG_TYPES } from '@services/baseService';

interface CreateActivityBodyType {
  leadUuid: string;
  activityId: string;
  activityAttributes: Record<string, string>;
}

export interface ListActivitiesProps {
  leadUuid: string,
  cursor?: string,
  limit?: number,
  activityIds: number[],
  activitySubtypes?: string[],
  timeFilter?: TimeFilterOptionsType
}

export interface CreateActivityResponse {
  success: boolean;
  data?: {
    Message: {
      Id: string; // lead activity uuid
      RelatedId: string; // lead uuid
    }
  }
}

export interface ActivitySummaryGroup {
  id: number;
  display_label: string;
  order: number;
  value: number;
  activity_ids: number[];
}

export interface ActivitySummarySubsection {
  id: number;
  name: string;
  description: string;
  icon: string;
  order: number;
  groups: ActivitySummaryGroup[];
}

export interface ActivitySummarySection {
  id: number;
  name: string;
  order: number;
  subsections: ActivitySummarySubsection[];
  hot_activities_data: {
    name: string;
    time: string;
  } | null;
}

export interface ActivitySummariesResponse {
  success: boolean;
  data: {
    sections: ActivitySummarySection[];
  };
}

const activityService = baseService.injectEndpoints({
  endpoints: (build) => ({
    listActivities: build.query<GetActivitiesByLeadUuidResponse, ListActivitiesProps>({
      query: (body) => ({
        method: 'POST',
        url: '/graphql',
        body: { query: getActivitiesByLeadUuid, variables: body },
      }),
      providesTags: [TAG_TYPES.leadActivityList],
      transformResponse: transformGraphqlResponse,
    }),
    deleteActivity: build.mutation<void, { activityUuid: string }>({
      query: ({ activityUuid }) => ({
        method: 'DELETE',
        url: `${V1_BASE_URL}lead_activities/${activityUuid}`,
      }),
      invalidatesTags: [TAG_TYPES.leadActivityList],
    }),
    createActivity: build.mutation<CreateActivityResponse, CreateActivityBodyType>({
      query: ({ leadUuid, activityId, activityAttributes }) => ({
        method: 'POST',
        url: `${V1_BASE_URL}leads/${leadUuid}/lead_activities/`,
        body: {
          activity_id: activityId,
          activity_attributes: activityAttributes,
        },
      }),
      invalidatesTags: [TAG_TYPES.leadActivityList],
    }),
  }),
});

export const {
  useLazyListActivitiesQuery,
  useDeleteActivityMutation,
  useCreateActivityMutation,
} = activityService;

export default activityService;
